/**
 * Mascot Domain Config — Centrale timings & thresholds voor het Djay-domein.
 *
 * DDD: alle "magic numbers" die voorheen verspreid stonden over
 * `FloatingDjay`, `easterEggs`, `useVariantUnlocks` en `DjayBubble`
 * komen hier samen. Eén plek om "voelt te traag/snel"-discussies te voeren.
 *
 * Audit-referentie: docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §4.
 */

/** Tijden in milliseconden voor alle Djay-interacties */
export const DJAY_TIMINGS = {
  // ── Long-press & rapid-tap ─────────────────────────────────
  /** Hoe lang ingedrukt voor het long-press menu opent */
  LONG_PRESS_MS: 500,
  /** Aantal taps binnen RAPID_TAP_WINDOW_MS dat het rapid-tap egg triggert */
  RAPID_TAP_THRESHOLD: 5,
  /** Tijdsvenster waarbinnen taps tellen */
  RAPID_TAP_WINDOW_MS: 2000,
  /** Cooldown na trigger waarin extra taps worden geabsorbeerd */
  RAPID_TAP_COOLDOWN_MS: 1500,
  // ── Proactive messages ─────────────────────────────────────
  /** Vertraging na mount voordat de welkomstboodschap verschijnt */
  WELCOME_DELAY_MS: 1000,
  /** Hoe lang de welkomstbubble blijft staan */
  WELCOME_BUBBLE_MS: 4000,
  /** Hoe lang de view-change-bubble blijft staan */
  VIEW_CHANGE_BUBBLE_MS: 3000,
  /** Hoe lang de idle-bubble blijft staan */
  IDLE_BUBBLE_MS: 5000,
  /** Inactiviteit voordat idle-bubble triggert */
  IDLE_TIMEOUT_MS: 2 * 60 * 1000,
  // ── Touch auto-cycle (per-item bubbles op no-hover devices) ─
  /** Wachttijd na panel-open voordat eerste item-bubble triggert */
  TOUCH_CYCLE_INITIAL_MS: 1800,
  /** Interval tussen item-bubbles in auto-cycle */
  TOUCH_CYCLE_INTERVAL_MS: 2500,
  // ── Bubble durations (gespecialiseerd) ─────────────────────
  /** Standaard auto-dismiss in DjayBubble (als geen prop wordt meegegeven) */
  BUBBLE_DEFAULT_DISMISS_MS: 8000,
  /** Daily tip blijft langer staan (meer tekst) */
  TIP_BUBBLE_MS: 6000,
  /** Easter-egg reactie zoals rapid-tap */
  EASTER_EGG_BUBBLE_MS: 4000,
  /** Konami code reactie */
  KONAMI_BUBBLE_MS: 5000,
  /** Time-based egg (nacht-uil, vroege-vogel) */
  TIME_EGG_DELAY_MS: 3000,
  TIME_EGG_BUBBLE_MS: 5000,
  // ── Pulse / animation ──────────────────────────────────────
  /** Aanbevolen pre-message pulse-tijd (attention pulse glow) */
  ATTENTION_PULSE_MS: 1000,
  /** Tap pulse fade-out */
  TAP_PULSE_MS: 500,
  /** Easter-egg label hoe lang in beeld */
  EASTER_EGG_LABEL_MS: 4000
} as const;

/** Drempelwaarden voor mastery, levels, XP — gedeeld tussen UI en unlock-logica */
export const DJAY_THRESHOLDS = {
  /** Module geldt als "beheerst" boven dit percentage (bron: MASTERY canonical) */
  MODULE_MASTERY_PCT: 85,
  // Variant unlocks
  PIXEL_QUESTIONS_ANSWERED: 100,
  BLAZE_CONSECUTIVE_FULL_WEEKS: 3,
  FROST_MODULES_MASTERED: 3,
  COSMIC_LEVEL: 15,
  SHADOW_XP: 30000
} as const;

/** Tijdvenster voor time-based easter eggs (uren, lokale tijd) */
export const DJAY_TIME_EGGS = {
  NACHT_UIL_FROM_HOUR: 22,
  NACHT_UIL_TO_HOUR: 5,
  VROEGE_VOGEL_FROM_HOUR: 5,
  VROEGE_VOGEL_TO_HOUR: 7
} as const;

/**
 * Aliases consumed by `easterEggs.ts`. Property names follow SCREAMING_SNAKE
 * to match the existing call-sites there (e.g. `RAPID_TAP_CONFIG.THRESHOLD`).
 * Both shapes point at the same source values in `DJAY_TIMINGS` /
 * `DJAY_TIME_EGGS` so there is no drift risk.
 */
export const RAPID_TAP_CONFIG = {
  THRESHOLD: DJAY_TIMINGS.RAPID_TAP_THRESHOLD,
  WINDOW_MS: DJAY_TIMINGS.RAPID_TAP_WINDOW_MS,
  COOLDOWN_MS: DJAY_TIMINGS.RAPID_TAP_COOLDOWN_MS
} as const;
export const TIME_EGG_HOURS = {
  /** Hour at/after which "nacht-uil" triggers (evening side) */
  NIGHT_OWL_START: DJAY_TIME_EGGS.NACHT_UIL_FROM_HOUR,
  /** Hour before which "nacht-uil" still applies (morning side) */
  NIGHT_OWL_END: DJAY_TIME_EGGS.NACHT_UIL_TO_HOUR,
  /** Hour before which "vroege-vogel" applies */
  EARLY_BIRD_END: DJAY_TIME_EGGS.VROEGE_VOGEL_TO_HOUR
} as const;